import React from 'react';
import { HelpCircle, Check } from 'lucide-react';

interface SectionProps {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  isComplete?: boolean;
  step?: number;
  totalSteps?: number;
  onHelpClick?: () => void;
}

/**
 * Seção do formulário do plano: número da etapa (✓ quando concluída),
 * título, descrição e botão de ajuda.
 */
export const Section: React.FC<SectionProps> = ({
  id,
  title,
  description,
  children,
  isComplete = false,
  step,
  totalSteps,
  onHelpClick,
}) => {
  return (
    <section
      id={id}
      className="scroll-mt-40"
      aria-labelledby={`${id}-title`}
    >
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 sm:px-8 lg:px-10 pt-7 pb-6 border-b border-slate-100 flex items-start gap-4">
          {step !== undefined && (
            <span
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                isComplete ? 'bg-emerald-600 text-white' : 'bg-[#08162D] text-white'
              }`}
              aria-hidden="true"
            >
              {isComplete ? <Check className="w-5 h-5" strokeWidth={3} /> : step}
            </span>
          )}
          <div className="flex-1 min-w-0">
            {step !== undefined && totalSteps && (
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Etapa {step} de {totalSteps}
                {isComplete && <span className="ml-2 text-emerald-700 normal-case tracking-normal">· concluída</span>}
              </p>
            )}
            <h2 id={`${id}-title`} className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight mt-0.5">
              {title}
            </h2>
            {description && (
              <p className="text-[15px] text-slate-600 mt-1">{description}</p>
            )}
          </div>
          {onHelpClick && (
            <button
              type="button"
              onClick={onHelpClick}
              className="flex-shrink-0 h-10 px-3 flex items-center gap-1.5 rounded-lg text-sm font-semibold text-slate-600 hover:text-[#08162D] hover:bg-slate-100 transition-colors"
              aria-label={`Ajuda para preencher: ${title}`}
            >
              <HelpCircle className="w-5 h-5" aria-hidden="true" />
              <span className="hidden sm:inline">Ajuda</span>
            </button>
          )}
        </div>

        <div className="px-6 sm:px-8 lg:px-10 py-7">
          {children}
        </div>
      </div>
    </section>
  );
};

export default Section;
